import {
	API_ENDPOINTS,
	DataTable,
	Icon,
	Icons,
	PageLayout,
	route,
	StringHelpers,
	useAuthorisationContext,
	useLangContext,
	Variants,
	Permissions,
	customToast,
	DateFormats,
	FormInput,
	ImageType,
	getRegistrationUrl,
} from 'carrier-fe';
import { useMemo } from 'react';
import { formatDate, subMonths } from 'date-fns';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

interface InstallationData {
	id: number;
	name: string;
	completion_date: string;
	customer_name: string;
	appliances: {
		serial_number: string;
		appliance_details: {
			full_name: string;
		};
	}[];
	address_line_1?: string,
	address_line_2?: string,
	address_line_3?: string,
	town_city?: string,
	state_county?: string,
	postcode_zipcode?: string,
	local_authority_code?: string,
	sub_country_name?: string,
	country_code_iso_3?: string,
	country?: string,
	installer_company?: {
		name: string
	};
	referer?: string;
}

function ReportingList() {
	const { crud, fields, responses } = useLangContext();
	const { hasPermissionAccess } = useAuthorisationContext();
	const [searchParams] = useSearchParams();

	const exportReport = async () => {
		const currentSearchParams = new URLSearchParams(window.location.search);
		const startDate = currentSearchParams.get('filter[start_date]');
		const endDate = currentSearchParams.get('filter[end_date]');

		try {
			await axios.get(
				route(
					API_ENDPOINTS.ADMIN.INSTALLATION_WARRANTY.EXPORT
				),
				{
					params: {
						'filter[start_date]': startDate,
						'filter[end_date]': endDate,
					},
				}
			)
			customToast({
				title:
					responses?.admin.reporting.export.success ||
					'Success. Report downloaded successfully.',
				variant: Variants.Success,
			});
		} catch (error: any) {
			customToast({
				title: error?.response?.data?.message || '',
				variant: Variants.Danger,
			});
		}
	};

	const handleOptionSelect = (
		action: string | ImageType | null,
		data: InstallationData
	) => {
		const actions: { [key: string]: () => void } = {
			view: () => {
				const url = new URL(`${getRegistrationUrl()}/installation/${data.id}`);
				url.searchParams.set('from', 'reporting');

				const searchFilter = searchParams.get('filter[search]');
				if (searchFilter) {
					url.searchParams.set('filter[search]', searchFilter);
				}

				window.open(url.toString(), '_self');
			}
		};

		if (typeof action === 'string' && actions[action]) {
			actions[action]();
		}
	};

	const items = useMemo(() => {
		if (!hasPermissionAccess(Permissions.ADMIN_REPORT_EXPORT)) return [];

		return [
			{
				label: StringHelpers.title(
					crud?.buttons?.export?.default || 'Export'
				),
				disabled: false,
				onClick: () => exportReport(),
				icon: <Icon icon={Icons.ADD} />,
			},
		];
	}, []);

	const colMapping = (data: InstallationData) => [

		{
			id: 'referer',
			label: StringHelpers.title(fields?.referer || 'Referer'),
			value: data?.referer || '-',
			sortable: true,
		},
		{
			id: 'company_name',
			label: StringHelpers.title(
				fields?.installer_company_name || 'Installer Company Name'
			),
			value: data?.name || '-',
			sortable: true,
		},
		{
			id: 'completion_date',
			label: StringHelpers.title(
				fields?.completion_date || 'Work Completion Date'
			),
			value: data?.completion_date || '-',
			sortable: true,
		},
		{
			id: 'address',
			label: StringHelpers.title(fields?.address || 'Address'),
			value: data?.address_line_1
				? [
					data?.address_line_1,
					data?.address_line_2,
					data?.town_city,
					data?.state_county,
				]
					.filter(Boolean) // Removes null, undefined, and empty strings
					.join(', ')
				: '-',
			sortable: false,
		},
		{
			id: 'postcode_zipcode',
			label: StringHelpers.title(
				fields?.postcode_zipcode || 'Postcode / Zipcode'
			),
			value: data?.postcode_zipcode || '-',
			sortable: false,
		},
		{
			id: 'contact_name',
			label: StringHelpers.title(fields?.contact_name || 'Contact Name'),
			value: data?.customer_name || '-',
			sortable: true,
		},
		{
			id: 'serial_number',
			label: StringHelpers.title(
				fields?.serial_number || 'Serial Number'
			),
			value: data?.appliances[0]?.serial_number || '-',
			sortable: false,
		},
		{
			id: 'product',
			label: StringHelpers.title(fields?.product || 'Product'),
			value: data?.appliances[0]?.['appliance_details']?.full_name || '-',
			sortable: false,
		},
		{
			id: 'actions',
			label: StringHelpers.title(fields?.actions || 'Actions'),
			value:
				<FormInput
					type="select"
					label={StringHelpers.title(fields?.actions || 'Action')}
					placeholder={StringHelpers.title(
						fields?.select || 'Select'
					)}
					name="actions"
					value=""
					onChange={(value: any) => {
						handleOptionSelect(value, data);
					}}
					options={[
						{
							label: StringHelpers.title(
								crud?.view || 'View'
							),
							value: 'view',
						},
					]}
					noMargin
				/>

		},
	];

	const title = useMemo(
		() =>
			StringHelpers.title(
				crud?.models.registration_report || 'Registration Reports'
			),
		[crud]
	);

	return (
		<PageLayout title={title}>
			<DataTable<InstallationData>
				routeEndpoint={route(
					API_ENDPOINTS.ADMIN.INSTALLATION_WARRANTY.INDEX
				)}
				colMapping={colMapping}
				items={items}
				filters={[
					{
						name: 'start_date',
						label: StringHelpers.title(
							fields?.start_date || 'Start Date'
						),
						type: 'date',
						value: formatDate(
							subMonths(new Date(), 3).setHours(0, 0, 0, 0),
							DateFormats.DB
						),
					},
					{
						name: 'end_date',
						label: StringHelpers.title(
							fields?.end_date || 'End Date'
						),
						type: 'date',
						value: formatDate(
							new Date().setHours(0, 0, 0, 0),
							DateFormats.DB
						),
					},
				]}
				incrementPaginationButtons
				variant={Variants.Info}
				helperTextItems={[
					fields.referer || "referer",
					fields.company_name || "company name",
					fields.postcode || "postcode",
					fields.contact_name|| "contact name",
					fields.serial_number || "serial number",
					fields.product || "product",
				]}
			/>
		</PageLayout>
	);
}

export default ReportingList;
